<script>
  import Button from '$lib/components/Button.svelte';
  import Modal from '$lib/components/Modal.svelte';
  import Form from '$lib/components/form/Form.svelte';
  import MultiSelect from '$lib/components/form/MultiSelect.svelte';
  import Option from '$lib/components/form/Option.svelte';
  import Select from '$lib/components/form/Select.svelte';
  import TextArea from '$lib/components/form/TextArea.svelte';
  import { closeModal, showToast } from '$lib/features/app/modals';
  import { logger } from '$lib/logger';
  import { useQueryClient } from '@tanstack/svelte-query';
  import { NOTE_TYPES } from '../../constants';
  import { useEditConsultationHistoryNoteMutation } from '../../service';

  /**
   * @typedef {Object} Props
   * @property {any} [note]
   * @property {any} areaOptions
   */

  /** @type {Props} */
  let { note = null, areaOptions } = $props();

  const queryClient = useQueryClient();
  const editSavedNoteMutation = useEditConsultationHistoryNoteMutation();
  let submittedLoading = false;

  let isFormDirty = $state(false);

  // svelte-ignore state_referenced_locally
  let noteText = $state(note.notes);
  // svelte-ignore state_referenced_locally
  let contactType = $state(note.contactType);

  // find consultationareaids based on areaoptions
  // svelte-ignore state_referenced_locally
  let selectedConsultationAreas = $state(
    note.consultationAreas.map(({ id }) => id),
  );
  let validationErrors = $state({});

  const noteTypeOptions = Object.entries(NOTE_TYPES).map(([value, label]) => ({
    value,
    label,
  }));

  const formIsValid = () => {
    validationErrors = {};
    if (!noteText) {
      validationErrors.noteText = 'journalanteckning saknas';
    }
    if (!contactType) {
      validationErrors.contactType = 'Anteckningstyp saknas';
    }
    // if there are any errors, return false
    if (Object.keys(validationErrors).length > 0) {
      return false;
    }
    // otherwise, all is good
    return true;
  };

  const handleSubmit = async () => {
    if (!formIsValid()) return;

    $editSavedNoteMutation.mutate(
      {
        noteId: note.id,
        note: noteText,
        contactType,
        consultationAreas: selectedConsultationAreas,
      },
      {
        onSuccess: () => {
          showToast('Journalen är redigerad', 'success');
          queryClient.invalidateQueries({ queryKey: ['notes'] });
          closeModal();
        },
        onError: (e) => {
          showToast(e.message, 'error');
          logger.error('Failed to edit consultation note', {
            err: e instanceof Error ? e : undefined,
          });
        },
      },
    );
  };

  function onModalClose() {
    if (isFormDirty) {
      if (
        window.confirm(
          'Du har osparade ändringar. Är du säker på att du vill stänga?',
        )
      ) {
        closeModal();
      }
    } else {
      closeModal();
    }
  }
</script>

<Modal title="Redigera journalen" size="md" handleClose={onModalClose}>
  <Form
    onformDirty={(dirty) => {
      isFormDirty = dirty;
    }}
    onsubmit={handleSubmit}
  >
    {#if note !== null}
      <div class="flex w-full flex-col gap-4">
        <TextArea
          tall={true}
          rows={5}
          type="text"
          bind:value={noteText}
          name="nodeText"
        />

        <MultiSelect
          name="selectedNoteAreas"
          placeholder="Hälsoområde"
          bind:value={selectedConsultationAreas}
        >
          {#if areaOptions}
            {#each areaOptions as option}
              <Option value={option.value}>{option.label}</Option>
            {/each}
          {/if}
        </MultiSelect>

        <Select
          name="selectedType"
          valid={!validationErrors.contactType}
          placeholder="Anteckningsttyp"
          bind:value={contactType}
          options={noteTypeOptions}
        />
      </div>
    {/if}
    <div class="w-full">
      <div class="mt-4 flex justify-end gap-4">
        <Button
          variant="secondary"
          iconLeft="cancel"
          type="button"
          onclick={onModalClose}
        >
          Avbryt
        </Button>
        <Button
          type="submit"
          class="w-28"
          loading={submittedLoading}
          variant="primary"
          disabled={noteText === null}
        >
          Spara
        </Button>
      </div>
    </div>
  </Form>
</Modal>
